import { collectMerchantHomeBindingParts, type MerchantHomeBindingPart } from "@/features/merchant-home/merchantHomeBindingSchema";
import type { UIMessage } from "ai";

import {
  projectConversationCreativeMediaPart,
  collectCreativeMediaJobReferences,
  type CreativeMediaJobReference,
  type ConversationCreativeMediaPart,
} from "@/features/chat/creativeMediaSchemas";
import { projectMerryChatExtensionViewModels } from "@/features/chat/chatExtensions";
import {
  projectConversationResourceGate,
  type ConversationResourceGate,
} from "@/features/chat/resourceAvailability";
import {
  projectExperienceStrategyProposalPart,
  type ExperienceStrategyProposalPart,
} from "@/features/experience-strategy/experienceStrategySchemas";

import {
  conversationQuoteWireSchema,
  conversationExecutionNodeWireSchema,
  toConversationExecutionNode,
  type ConversationMessage,
  type ConversationExecutionNode,
  type ConversationQuoteWire,
  type ConversationTurn,
  type ConversationUIMessageWire,
} from "@/features/chat/conversationSchemas";
import type { MerryChatExtensionViewModel } from "@/shared/ui/chatExtensionTypes";

export interface ConversationQuoteDraft {
  endOffset: number;
  sourceContentHash: string;
  sourceMessageId: string;
  sourceProjection?: "rendered_text.v1";
  sourceProjectionText?: string;
  sourceSequence: number;
  startOffset: number;
  text: string;
}

export interface ConversationRenderMessage {
  contentHash: string | null;
  createdAt: string | null;
  id: string;
  executionNodes: ReadonlyArray<ConversationExecutionNode>;
  extensions: ReadonlyArray<MerryChatExtensionViewModel>;
  creativeMediaParts: ReadonlyArray<ConversationCreativeMediaPart>;
  creativeMediaJobReferences?: ReadonlyArray<CreativeMediaJobReference>;
  experienceStrategyProposalParts: ReadonlyArray<ExperienceStrategyProposalPart>;
  merchantHomeBindingParts: ReadonlyArray<MerchantHomeBindingPart>;
  quote: ConversationQuoteWire | null;
  resourceGate: ConversationResourceGate | null;
  role: "assistant" | "user";
  sequence: number | null;
  text: string;
}

export interface ConversationRenderTurn {
  assistantMessages: ReadonlyArray<ConversationRenderMessage>;
  endSequence: number | null;
  id: string;
  startSequence: number | null;
  userMessage: ConversationRenderMessage;
}

export interface ConversationAnswerSource {
  evidenceId?: string;
  supportStatus?: "DIRECT_SUPPORT" | "DIRECT_OBSERVATION" | "TRANSFERABLE_HYPOTHESIS" | "HYPOTHESIS" | "UNVERIFIED";
  excerpt?: string;
  iconUrl?: string;
  id: string;
  label: string;
  observedAt?: string;
  url: string;
}

interface ConversationPage<T> {
  readonly pages: ReadonlyArray<T>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function collectSelectableParts(value: unknown, parts: string[]) {
  if (!Array.isArray(value)) return;
  for (const child of value) {
    if (
      isRecord(child) &&
      child.type === "text" &&
      typeof child.text === "string" &&
      child.text.length > 0
    ) {
      parts.push(child.text);
    }
  }
}

export function getConversationMessageSelectableText(
  message: ConversationUIMessageWire,
) {
  const parts: string[] = [];
  collectSelectableParts(message.parts, parts);
  if (
    parts.length === 0 &&
    "content" in message &&
    typeof message.content === "string" &&
    message.content.length > 0
  ) {
    parts.push(message.content);
  }
  return parts.join("\n");
}

export function flattenConversationMessagePages(
  data:
    | ConversationPage<{ messages: ReadonlyArray<ConversationMessage> }>
    | undefined,
) {
  const messages = new Map<string, ConversationMessage>();
  for (const page of data?.pages ?? []) {
    for (const message of page.messages) {
      messages.set(message.id, message);
    }
  }
  return [...messages.values()].sort((left, right) =>
    left.sequence === right.sequence
      ? left.id.localeCompare(right.id)
      : left.sequence - right.sequence,
  );
}

export function flattenConversationTurnPages(
  data:
    | ConversationPage<{ turns: ReadonlyArray<ConversationTurn> }>
    | undefined,
) {
  const turns = new Map<string, ConversationTurn>();
  for (const page of data?.pages ?? []) {
    for (const turn of page.turns) {
      turns.set(turn.turnId, turn);
    }
  }
  return [...turns.values()].sort((left, right) =>
    left.startSequence === right.startSequence
      ? left.turnId.localeCompare(right.turnId)
      : left.startSequence - right.startSequence,
  );
}

export function toChatUIMessage(message: ConversationMessage): UIMessage | null {
  if (message.role !== "assistant" && message.role !== "user") {
    return null;
  }
  const text = getConversationMessageSelectableText(message.message);
  const creativeMediaParts = collectCreativeMediaParts(
    message.message.parts,
    message.id,
  );
  const resourceGateParts = collectResourceGateParts(message.message.parts);
  const experienceStrategyProposalParts =
    collectExperienceStrategyProposalParts(message.message.parts);
  return {
    id: message.id,
    metadata: message.message.metadata,
    parts: [
      ...(text ? [{ text, type: "text" as const }] : []),
      ...creativeMediaParts,
      ...collectCreativeMediaJobReferences(message.message.parts).map((data) => ({
        type: "data-creative-media-job" as const, id: data.job_id, data,
      })),
      ...collectMerchantHomeBindingParts(message.message.parts),
      ...experienceStrategyProposalParts.map(
        (part) => part as unknown as UIMessage["parts"][number],
      ),
      ...resourceGateParts,
    ],
    role: message.role,
  };
}

function collectExperienceStrategyProposalParts(value: unknown) {
  if (!Array.isArray(value)) return [];
  const result: ExperienceStrategyProposalPart[] = [];
  const seen = new Set<string>();
  for (const part of value) {
    const projected = projectExperienceStrategyProposalPart(part);
    if (!projected || seen.has(projected.data.proposal_id)) continue;
    seen.add(projected.data.proposal_id);
    result.push(projected);
  }
  return result;
}

function experienceStrategyProposalPartsFromUIMessage(message: UIMessage) {
  return collectExperienceStrategyProposalParts(message.parts);
}

function collectResourceGateParts(value: unknown) {
  if (!Array.isArray(value)) return [];
  return value.flatMap((part) => {
    const projected = projectConversationResourceGate(part);
    return projected
      ? [projected as unknown as UIMessage["parts"][number]]
      : [];
  });
}

function resourceGateFromUIMessage(message: UIMessage) {
  for (let index = message.parts.length - 1; index >= 0; index -= 1) {
    const projected = projectConversationResourceGate(message.parts[index]);
    if (projected) return projected;
  }
  return null;
}

function collectCreativeMediaParts(
  value: unknown,
  legacyGroupId: string,
): ConversationCreativeMediaPart[] {
  if (!Array.isArray(value)) return [];
  const result: ConversationCreativeMediaPart[] = [];
  const seen = new Set<string>();
  for (const child of value) {
    const part = projectConversationCreativeMediaPart(child, legacyGroupId);
    if (!part) continue;
    const key = `${part.data.job_id}:${part.data.resource_id}:${part.data.role}`;
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(part);
  }
  return result.sort((left, right) =>
    left.data.ordinal === right.data.ordinal
      ? left.data.resource_id.localeCompare(right.data.resource_id)
      : left.data.ordinal - right.data.ordinal,
  );
}

function creativeMediaPartsFromUIMessage(message: UIMessage) {
  return collectCreativeMediaParts(message.parts, message.id);
}

function visibleTextFromUIMessage(message: UIMessage) {
  const parts: string[] = [];
  collectSelectableParts(message.parts, parts);
  return parts.join("\n");
}

function extensionsFromUIMessage(message: UIMessage) {
  if (!isRecord(message.metadata)) return [];
  return projectMerryChatExtensionViewModels(message.metadata.extensions);
}

function liveExecutionNodesFromUIMessage(message: UIMessage) {
  const nodes: ConversationExecutionNode[] = [];
  for (const part of message.parts) {
    if (!isRecord(part) || part.type !== "data-execution-node") continue;
    const candidate = isRecord(part.data) ? { ...part.data } : null;
    if (!candidate) continue;
    if (candidate.assistant_message_id === "") {
      candidate.assistant_message_id = message.id;
    }
    const parsed = conversationExecutionNodeWireSchema.safeParse(candidate);
    if (!parsed.success) continue;
    nodes.push(toConversationExecutionNode(parsed.data));
  }
  return nodes;
}

function safeAnswerSourceURL(value: string | undefined) {
  if (!value) return null;
  try {
    const parsed = new URL(value);
    if (
      (parsed.protocol !== "http:" && parsed.protocol !== "https:") ||
      !parsed.hostname ||
      parsed.username ||
      parsed.password
    ) {
      return null;
    }
    parsed.hash = "";
    return parsed;
  } catch {
    return null;
  }
}

function safeAnswerSourceIconURL(value: string | undefined) {
  if (!value) return undefined;
  if (value.startsWith("/") && !value.startsWith("//")) return value;
  try {
    const parsed = new URL(value);
    if (
      parsed.protocol !== "https:" ||
      !parsed.hostname ||
      parsed.username ||
      parsed.password
    ) {
      return undefined;
    }
    return parsed.toString();
  } catch {
    return undefined;
  }
}

export function projectAnswerSources(
  executionNodes: ReadonlyArray<ConversationExecutionNode>,
): ConversationAnswerSource[] {
  const seen = new Set<string>();
  const projected: ConversationAnswerSource[] = [];
  const nodes = [...executionNodes].sort((left, right) =>
    left.sequence === right.sequence
      ? left.nodeId.localeCompare(right.nodeId)
      : left.sequence - right.sequence,
  );

  for (const node of nodes) {
    for (const source of node.sources) {
      if (source.kind !== "web") continue;
      const url = safeAnswerSourceURL(source.locator);
      if (!url) continue;
      const normalizedURL = url.toString();
      const id = source.evidenceId ? `${source.evidenceId}:${normalizedURL}` : source.resourceId?.trim() || normalizedURL;
      if (seen.has(id)) continue;
      seen.add(id);
      const iconUrl = safeAnswerSourceIconURL(source.iconUrl);
      projected.push({
        ...(source.evidenceId ? { evidenceId: source.evidenceId } : {}),
        ...(source.supportStatus ? { supportStatus: source.supportStatus } : {}),
        ...(source.excerpt ? { excerpt: source.excerpt } : {}),
        ...(iconUrl ? { iconUrl } : {}),
        id,
        label: source.label.trim() || url.hostname,
        ...(source.observedAt ? { observedAt: source.observedAt } : {}),
        url: normalizedURL,
      });
    }
  }

  return projected;
}

function mergeExecutionNodes(
  message: UIMessage,
  persisted: ReadonlyArray<ConversationExecutionNode>,
) {
  const nodes = new Map<string, ConversationExecutionNode>();
  for (const node of persisted) {
    if (node.assistantMessageId === message.id) nodes.set(node.nodeId, node);
  }
  for (const node of liveExecutionNodesFromUIMessage(message)) {
    const current = nodes.get(node.nodeId);
    if (!current || current.revision <= node.revision) {
      nodes.set(node.nodeId, node);
    }
  }
  return [...nodes.values()].sort((left, right) =>
    left.sequence === right.sequence
      ? left.nodeId.localeCompare(right.nodeId)
      : left.sequence - right.sequence,
  );
}

const terminalExecutionStatuses = new Set([
  "canceled",
  "failed",
  "succeeded",
]);

function mergeProjectedExecutionNodes(
  messages: ReadonlyArray<ConversationRenderMessage>,
) {
  const nodes = new Map<string, ConversationExecutionNode>();
  for (const message of messages) {
    for (const node of message.executionNodes) {
      const current = nodes.get(node.nodeId);
      const shouldReplace =
        !current ||
        current.revision < node.revision ||
        (current.revision === node.revision &&
          !terminalExecutionStatuses.has(current.status) &&
          terminalExecutionStatuses.has(node.status));
      if (shouldReplace) nodes.set(node.nodeId, node);
    }
  }
  return [...nodes.values()].sort((left, right) =>
    left.sequence === right.sequence
      ? left.nodeId.localeCompare(right.nodeId)
      : left.sequence - right.sequence,
  );
}

function collapseTurnAssistantMessages(
  messages: ReadonlyArray<ConversationRenderMessage>,
  isGenerating?: boolean,
) {
  if (messages.length <= 1) {
    return isGenerating === false
      ? messages.map((message) => ({
          ...message,
          executionNodes: message.executionNodes.filter(
            (node) => node.status !== "queued" && node.status !== "running",
          ),
        }))
      : messages;
  }

  const persisted = messages.filter((message) => message.sequence !== null);
  const canonical =
    persisted.reduce<ConversationRenderMessage | null>((latest, message) => {
      if (!latest) return message;
      return (message.sequence ?? -1) >= (latest.sequence ?? -1)
        ? message
        : latest;
    }, null) ??
    [...messages].reverse().find((message) => message.text.trim().length > 0) ??
    messages[0];

  if (!canonical) return [];

  // 流式开始时 data part 可能先形成一个临时 assistant message，随后正文又形成
  // 另一个 message。正式消息入库后，只允许属于正式 assistant ID 的节点继续投影，
  // 避免旧临时 message 永久留下“正在处理”。
  const executionSources =
    persisted.length === 0
      ? messages
      : messages.filter(
          (message) =>
            message.id === canonical.id ||
            message.executionNodes.some(
              (node) => node.assistantMessageId === canonical.id,
            ),
        );
  const visibleText =
    canonical.text ||
    [...messages]
      .reverse()
      .find((message) => message.text.trim().length > 0)?.text ||
    "";
  const creativeMediaParts = collectCreativeMediaParts(
    messages.flatMap((message) => message.creativeMediaParts),
    canonical.id,
  );
  const experienceStrategyProposalParts = new Map<
    string,
    ExperienceStrategyProposalPart
  >();
  for (const message of messages) {
    for (const part of message.experienceStrategyProposalParts) {
      experienceStrategyProposalParts.set(part.data.proposal_id, part);
    }
  }

  const mergedExecutionNodes = mergeProjectedExecutionNodes(executionSources);
  const visibleExecutionNodes =
    isGenerating === false
      ? mergedExecutionNodes.filter(
          (node) => node.status !== "queued" && node.status !== "running",
        )
      : mergedExecutionNodes;

  return [
    {
      ...canonical,
      creativeMediaParts,
      creativeMediaJobReferences: messages.flatMap((message) => message.creativeMediaJobReferences ?? []),
      merchantHomeBindingParts: collectMerchantHomeBindingParts(messages.flatMap(message => message.merchantHomeBindingParts)),
      experienceStrategyProposalParts: [
        ...experienceStrategyProposalParts.values(),
      ],
      executionNodes: visibleExecutionNodes,
      resourceGate:
        [...messages]
          .reverse()
          .find((message) => message.resourceGate)?.resourceGate ?? null,
      text: visibleText,
    },
  ];
}

export function mergeConversationUIMessages(
  history: ReadonlyArray<ConversationMessage>,
  live: ReadonlyArray<UIMessage>,
  options: { preferPersisted?: boolean } = {},
) {
  const messages = new Map<string, UIMessage>();
  const sequenceById = new Map(history.map((message) => [message.id, message.sequence]));
  const liveOrderById = new Map<string, number>();
  for (const message of history) {
    const uiMessage = toChatUIMessage(message);
    if (uiMessage) messages.set(uiMessage.id, uiMessage);
  }
  for (const [index, message] of live.entries()) {
    if (message.role === "assistant" || message.role === "user") {
      liveOrderById.set(message.id, index);
      if (!options.preferPersisted || !messages.has(message.id)) {
        messages.set(message.id, message);
      }
    }
  }
  return [...messages.values()].sort((left, right) => {
    const leftSequence = sequenceById.get(left.id);
    const rightSequence = sequenceById.get(right.id);
    if (leftSequence !== undefined && rightSequence !== undefined) {
      return leftSequence - rightSequence || left.id.localeCompare(right.id);
    }
    if (leftSequence !== undefined) return -1;
    if (rightSequence !== undefined) return 1;
    return (liveOrderById.get(left.id) ?? 0) - (liveOrderById.get(right.id) ?? 0);
  });
}

function quoteFromMetadata(message: ConversationUIMessageWire) {
  const candidate = message.metadata?.quote;
  const parsed = conversationQuoteWireSchema.safeParse(candidate);
  return parsed.success ? parsed.data : null;
}

export function buildConversationRenderTurns(
  history: ReadonlyArray<ConversationMessage>,
  live: ReadonlyArray<UIMessage>,
  executionNodes: ReadonlyArray<ConversationExecutionNode> = [],
  options: { isGenerating?: boolean } = {},
) {
  const historyById = new Map(history.map((message) => [message.id, message]));
  const messages = mergeConversationUIMessages(history, live);
  const turns: ConversationRenderTurn[] = [];
  let current: ConversationRenderTurn | null = null;

  for (const message of messages) {
    if (message.role !== "assistant" && message.role !== "user") continue;
    const historyMessage = historyById.get(message.id);
    const renderMessage: ConversationRenderMessage = {
      contentHash: historyMessage?.contentHash ?? null,
      merchantHomeBindingParts: message.role === "assistant" ? collectMerchantHomeBindingParts(message.parts) : [],
      createdAt: historyMessage?.createdAt ?? null,
      creativeMediaJobReferences: message.role === "assistant" ? collectCreativeMediaJobReferences(message.parts) : [],
      creativeMediaParts:
        message.role === "assistant"
          ? creativeMediaPartsFromUIMessage(message)
          : [],
      experienceStrategyProposalParts:
        message.role === "assistant"
          ? experienceStrategyProposalPartsFromUIMessage(message)
          : [],
      executionNodes:
        message.role === "assistant"
          ? mergeExecutionNodes(message, executionNodes)
          : [],
      extensions:
        message.role === "user" ? extensionsFromUIMessage(message) : [],
      id: message.id,
      quote: historyMessage ? quoteFromMetadata(historyMessage.message) : null,
      resourceGate:
        message.role === "assistant" ? resourceGateFromUIMessage(message) : null,
      role: message.role,
      sequence: historyMessage?.sequence ?? null,
      text: visibleTextFromUIMessage(message),
    };

    if (message.role === "user") {
      current = {
        assistantMessages: [],
        endSequence: renderMessage.sequence,
        id: message.id,
        startSequence: renderMessage.sequence,
        userMessage: renderMessage,
      };
      turns.push(current);
      continue;
    }

    if (!current) continue;
    current.assistantMessages = [...current.assistantMessages, renderMessage];
    if (renderMessage.sequence !== null) {
      current.endSequence = renderMessage.sequence;
    }
  }

  return turns.map((turn, index) => ({
    ...turn,
    assistantMessages: collapseTurnAssistantMessages(
      turn.assistantMessages,
      options.isGenerating === undefined
        ? undefined
        : index === turns.length - 1
          ? options.isGenerating
          : false,
    ),
  }));
}

export function latestConversationResourceGate(
  turns: ReadonlyArray<ConversationRenderTurn>,
) {
  return turns.at(-1)?.assistantMessages.at(-1)?.resourceGate ?? null;
}

export function latestLengthLimitedConversationTurn(
  turns: ReadonlyArray<ConversationTurn>,
  history: ReadonlyArray<ConversationMessage>,
) {
  const interrupted = latestFailedFinishConversationTurn(turns, history);
  if (interrupted?.finishReason !== "length") return null;
  const assistant = history.find((message) => message.id === interrupted.assistantMessageId);
  if (
    !assistant || assistant.role !== "assistant" ||
    !getConversationMessageSelectableText(assistant.message).trim()
  ) return null;
  return { assistantMessageId: assistant.id, turnId: interrupted.turnId };
}

export function latestFailedFinishConversationTurn(
  turns: ReadonlyArray<ConversationTurn>,
  history: ReadonlyArray<ConversationMessage>,
) {
  const turn = turns.at(-1);
  if (turn?.status !== "failed" || !turn.latestAssistantMessageId) return null;
  const assistant = history.find((message) => message.id === turn.latestAssistantMessageId);
  if (!assistant || assistant.role !== "assistant") return null;
  const reason = assistant.message.metadata?.finish_reason;
  if (reason !== "length" && reason !== "content-filter" && reason !== "error" &&
    reason !== "other" && reason !== "unknown") return null;
  return { assistantMessageId: assistant.id, finishReason: reason, turnId: turn.turnId };
}

export function buildMinimapPreview(turn: ConversationTurn) {
  return {
    accessibleLabel: `跳转到对话：${turn.userPreview}`,
    id: turn.turnId,
    preview: {
      meta: turn.assistantPreview || turn.status,
      summary: turn.userPreview,
    },
  };
}

async function sha256Hex(value: string) {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(value),
  );
  return [...new Uint8Array(digest)]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

export async function buildConversationQuote(
  draft: ConversationQuoteDraft,
  scope: { conversationId: string; organizationId: string },
) {
  const common = {
    conversation_id: scope.conversationId,
    end_offset: draft.endOffset,
    offset_unit: "utf16",
    organization_id: scope.organizationId,
    source_content_hash: draft.sourceContentHash,
    source_message_id: draft.sourceMessageId,
    source_sequence: draft.sourceSequence,
    start_offset: draft.startOffset,
    text: draft.text,
    text_hash: await sha256Hex(draft.text),
  } as const;
  if (draft.sourceProjection && draft.sourceProjectionText !== undefined) {
    return conversationQuoteWireSchema.parse({
      ...common,
      schema: "conversation_quote.v2",
      source_projection: draft.sourceProjection,
      source_projection_hash: await sha256Hex(draft.sourceProjectionText),
    });
  }
  return conversationQuoteWireSchema.parse({
    ...common,
    schema: "conversation_quote.v1",
  });
}
